import { useEffect, useRef, useState } from 'react';
import { InputField, PinCodeField } from '@/components/formParts';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { API_BASE_URL, WP_URL } from '@/config';
import { useCookies } from 'react-cookie';
import { ClipLoader } from 'react-spinners';
import { linkToIframe } from '@/utiils';
 import { setLoginCookies } from '@/lib/sessionCookie'; 

const schema = z
  .object({
    email: z.string().min(1, { message: 'メールアドレスは必須です' })
            .email({ message: 'メールアドレス形式が不正です' }),
    password: z.string().min(1, { message: 'パスワードは必須です' }),
    // 非表示時も扱えるよう optional。表示時は下の disabled 判定で 6桁を要求
    code: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.code && !/^\d{6}$/.test(data.code)) {
      ctx.addIssue({
        code: 'custom',
        path: ['code'],
        message: '認証コードは数字6桁で入力してください',
      });
    }
  });

export type LoginFormData = z.infer<typeof schema>;

const LoginForm = ({ onComplete }: { onComplete: () => void }) => {
  const intervalTime = 30;
  const cookieMaxAge = 90 * 24 * 60 * 60; // 90日
  const cookieLoginMaxAge =  2 * 30 * 60; // 3600秒
  const [isLoading, setIsLoading] = useState(false);
  const [cookies, setCookie] = useCookies();
  const [skipVerify, setSkipVerify] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [countdownTimer, setCountdownTimer] = useState(intervalTime);
  const [apiError, setApiError] = useState<string | null>(null);

  const refVerifyContainer = useRef<HTMLDivElement>(null);

  const {
    register,
    watch,
    getValues,
    setValue,
    reset,
    trigger,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<LoginFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema),
    shouldUnregister: true,                          // 非表示の code を未登録化
    defaultValues: { email: '', password: '', code: '' },
  });

  // 前回ログイン済みならスキップ
  useEffect(() => {
    if (cookies.lastLogin) setSkipVerify(true);
  }, [cookies]);

  // 認証画面へ遷移したらスクロール＆タイマー
  useEffect(() => {
    if (!isVerified) return;
    refVerifyContainer.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setCountdownTimer(intervalTime);
    const timer = setInterval(() => {
      setCountdownTimer((prev) => (prev <= 0 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [isVerified]);

  const onVerify = async () => {
    if (skipVerify) {
      onLogin();
      return;
    }
    setIsLoading(true);
    setApiError(null); // ★追加：毎回クリア
    try {
      const username = '2chiiki-stg-wp';
      const password = 'DNt~VqC*N.-5';
      const basicAuth = btoa(`${username}:${password}`);

      const res = await fetch(`${API_BASE_URL}/v1/user/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${basicAuth}`,
        },
        body: JSON.stringify({ email: watch('email'), password: watch('password') }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (res.status === 429) {
          setApiError(
            err.error ||
              '一定回数以上ログインに失敗したため、しばらく時間をおいて再度お試しください。'
          );
          return;
        }
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      await res.json();
      setIsVerified(true);
    } catch (e: any) {
      alert('メール送信に失敗しました: ' + (e?.message || '不明なエラー'));
    } finally {
      setIsLoading(false);
    }
  };

  // 「ログインからやり直す」
  const onReset = () => {
    setIsVerified(false);
    setApiError(null); // ★追加：リセット時に消す
    // 値・エラー・touched を完全リセット → 再入力でボタンが有効化される
    reset({ email: '', password: '', code: '' }, {
      keepErrors: false,
      keepDirty: false,
      keepTouched: false,
      keepIsSubmitted: false,
    });
    clearErrors();
  };

  const onResendCode = () => {
    setIsVerified(false);
    onVerify();
  };

  const onLogin = async () => {
    setIsLoading(true);
    try {
      const username = '2chiiki-stg-wp';
      const password = 'DNt~VqC*N.-5';
      const basicAuth = btoa(`${username}:${password}`);

      const res = await fetch(`${API_BASE_URL}/v1/user/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${basicAuth}`,
        },
        body: JSON.stringify(getValues()),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        // ★追加：429 のときは画面表示
        if (res.status === 429) {
          setApiError(
            err.error ||
              '一定回数以上ログインに失敗したため、しばらく時間をおいて再度お試しください。'
          );
          return;
        }
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (!data.token) throw new Error('トークンが返されませんでした');

      localStorage.setItem('token', data.token);
      setCookie('lastLogin', 'true', {
        path: '/',
        maxAge: cookieMaxAge,
        secure: true,
        sameSite: 'strict',
      });
      // ログイン成功時にサーバーが ak_auth を Set-Cookie する前提
      //document.cookie = `ak_logged_in=1; Path=/; Max-Age=${cookieLoginMaxAge}; Secure; SameSite=Lax`;
      setLoginCookies(cookieLoginMaxAge);
      onComplete();
    } catch (e: any) {
      alert('ログインに失敗しました: ' + ("メールアドレス、パスワードをご確認下さい" || '不明なエラー'));
    } finally {
      setIsLoading(false);
    }
  };

  const onPasswordReset = () => {
    linkToIframe('/user/password_reminder', { isIframeUrl: WP_URL + '/password_reminder' });
  };

  // ボタン活性条件
  const emailVal = watch('email');
  const pwVal = watch('password');
  const codeVal = watch('code') || '';
  const codeOK = /^\d{6}$/.test(codeVal);

  const loginDisabled = !isValid || isLoading || isVerified;          // 初回「ログイン」
  const inquiryDisabled = !isValid || !codeOK || isLoading;           // 認証後「照会」

  return (
    <form>
      <p className="m-form-title">BLUE SKY LIFE 会員ログイン</p>
      {apiError && (
        <p className="mt-4 text-center text-red-600">
          {apiError}
        </p>
      )}

      <div className="flex flex-col gap-8 mt-8 xmd:w-[64rem]">
        <InputField
          label="メールアドレス"
          type="email"
          register={register('email')}
          errors={errors}
          className={isVerified ? 'protected' : ''}
          autoComplete="username"
        />

        <InputField
          label="パスワード"
          subLabel="（大文字/小文字のアルファベットと記号を組み合わせた8桁以上）"
          type="password"
          register={register('password')}
          errors={errors}
          className={isVerified ? 'protected' : ''}
          autoComplete="current-password"
        />

        <div className="flex justify-center mt-4">
          {!isVerified && (
            <button
              type="button"
              className="m-btn outlined btn-forest"
              disabled={loginDisabled || !emailVal || !pwVal}
              onClick={onVerify}
            >
              <em className="m-icon-login -mt-1 mr-2 text-[2.4rem]" />
              <span>ログイン</span>
            </button>
          )}
          {isVerified && (
            <button type="button" className="m-btn outlined" onClick={onReset}>
              ログインからやり直す
            </button>
          )}
        </div>

        {!isVerified && (
          <div className="text-center mt-4">
            <button type="button" className="underline" onClick={onPasswordReset}>
              パスワードをお忘れの方
            </button>
          </div>
        )}

        {!isVerified && isLoading && (
          <div className="flex justify-center">
            <ClipLoader color="#000" loading={isLoading} />
          </div>
        )}

        {isVerified && (
          <>
            <div
              className="text-center bg-yellow-100 mt-6 py-8 px-2 rounded-lg border border-yellow-300"
              ref={refVerifyContainer}
            >
              <p>
                <strong>{emailVal}</strong>
                宛に認証コードを送信しました。<br />
                メールに記載の認証コードをご入力ください。
              </p>
            </div>

            <div className="flex flex-col gap-8">
              <div className="mx-auto">
                {/* <PinCodeField
                  label="認証コード"
                  register={register('code')}
                  errors={errors}
                  length={6}
                  value={codeVal}
                  onChange={(val) => setValue('code', val, { shouldValidate: true })}
                />
              */}
              <InputField
                label="認証コード"
                type="tel"
                inputMode="numeric"
                autoComplete="one-time-code"
                {...register('code')}
                value={watch('code') || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const v = (e.target.value || '').replace(/\D/g, '').slice(0, 6);
                  setValue('code', v, { shouldValidate: true, shouldDirty: true });
                  // trigger('code'); // ここで即時バリデしたいなら有効化
                }}
              />
              </div>
              <div className="flex justify-center">
                <button
                  type="button"
                  className="m-btn fill"
                  onClick={onLogin}
                  disabled={inquiryDisabled}
                >
                  照会
                </button>
              </div>

              {isLoading && (
                <div className="flex justify-center">
                  <ClipLoader color="#000" loading={isLoading} />
                </div>
              )}
            </div>

            <div className="text-center bg-gray-100 py-8 px-2 rounded-lg border border-gray-300">
              <p>
                ※認証コードの再送信は、下のボタンを押してください。<br />
                {countdownTimer > 0 && `(再送可能まであと${countdownTimer}秒)`}
              </p>
              <div className="flex justify-center mt-4">
                <button
                  type="button"
                  className="m-btn btn-small outlined"
                  onClick={onResendCode}
                  disabled={countdownTimer > 0}
                >
                  認証コードを再送信
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </form>
  );
};

export default LoginForm;
